/**
 * App.tsx — composition root del demo conversacional Hipermaxi.
 *
 * 2026-05-08 — el demo es de CONSULTA, no de venta. No cart, no pedido. La
 * farmacéutica del mostrador hace consultas conversacionales sobre cada
 * cliente; el agente sugiere productos con SKU+lab+presentación + cross-sells
 * clínicos. Click en alternativa o complemento dispara nueva consulta sobre
 * ese SKU/PA (más conversacional que add-to-cart).
 *
 * Bug fix MANDATORIO app.jsx:44 — perfil default null (NO "adult").
 * Backend GUARD-03 ya rechaza queries sin perfil; este default null alinea
 * frontend con backend (defense-in-depth GUARD-03).
 *
 * Composition: Header + ProfileChips + (WelcomeScreen | ChatStream) +
 *              QuickChipsRow + Composer
 *
 * Drimian P6: el cómputo de dosis vive en backend (POS-02). El frontend solo
 * lee `card.dosis` y multiplica days × frequency_h para display.
 * GUARD-04: card.disclaimer renderizado literal (NO regenerar).
 */
import { useState, useRef, useEffect } from 'react'
import type { ProfileType } from './types/profile'
import { useChatStore } from './store/chat'
import { useSessionStore } from './store/session'
import { useDeviceCreds } from './hooks/useDeviceCreds'
import { resetSession, setProfile as apiSetProfile } from './api/session'
import { applyProfileTheme } from './theme'
import { Header } from './components/Header'
import { AccionesProducto } from './components/AccionesProducto'
import { WelcomeScreen } from './components/WelcomeScreen'
import { ProfileChips } from './components/ProfileChips'
import { ChatStream } from './components/ChatStream'
import { Composer } from './components/Composer'
import { QuickChipsRow } from './components/QuickChipsRow'
import { PanelPaciente } from './components/PanelPaciente'
import { FichaPanel } from './components/FichaPanel'
import { useMediaQuery } from './hooks/useMediaQuery'
import { pedirAccion, type AccionCaso } from './api/accion'
import { nuevoId } from './utils/nuevoId'
import type { Message } from './store/chat'

// Quick chips inferiores — términos cortos como apunte rápido en mostrador,
// 3ra persona sobre el cliente (la regente no es la paciente).
const QUICK_CHIPS = [
  'dolor de garganta',
  'fiebre',
  'tos seca',
  'dolor de cabeza',
  'diarrea',
  'vómitos',
  'acidez',
  'alergia',
  'insomnio',
]

const PERFIL_POR_CHIP: Record<string, ProfileType> = {
  'Niño 0-4': 'child_3y',
  'Niño 5-7': 'child_5y',
  'Niño 8-11': 'child_8y',
  'Niño 12+': 'child_12y',
}

export function App() {
  // Bug fix: profile default null. NO useState("adult"). GUARD-03 defense-in-depth.
  const profile = useSessionStore((s) => s.profile)
  const setProfileLocal = useSessionStore((s) => s.setProfile)
  const session_id = useSessionStore((s) => s.session_id)

  const messages = useChatStore((s) => s.messages)
  const sseState = useChatStore((s) => s.sseState)
  const sendChat = useChatStore((s) => s.send)
  const resetChat = useChatStore((s) => s.reset)
  const accionar = useChatStore((s) => s.accionar)
  const restaurar = useChatStore((s) => s.restaurar)

  const creds = useDeviceCreds()
  const [input, setInput] = useState('')
  const lastInputRef = useRef('')

  // 2026-05-12 — UX Elvio: tematización por perfil. Cuando cambia el
  // profile, mutamos los tokens OKLCH en :root y todo el UI (chips active,
  // bubbles, product card pill counter, fondo) se tinea coherentemente.
  useEffect(() => {
    applyProfileTheme(profile)
  }, [profile])

  const started = messages.length > 0

  // 2026-10-01 — página completa: ≥900 px panel del paciente a la izquierda;
  // ≥1180 px además la ficha del producto fija a la derecha. Debajo, una columna.
  // 2026-10-12 — un solo modo (Elvio): el de Conversación se quitó
  const conPanel = useMediaQuery('(min-width: 900px)')
  const conFicha = useMediaQuery('(min-width: 1180px)')
  const isBusy = ['classifying', 'searching', 'thinking', 'middleware_block'].includes(sseState.status)

  // Ficha en el panel: la del último producto sugerido, salvo que la regente toque otra.
  const [fichaIdx, setFichaIdx] = useState<number | null>(null)
  const ultimaFicha = (() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === 'staff' && messages[i].card) return i
    return null
  })()
  useEffect(() => {
    setFichaIdx(ultimaFicha)
  }, [ultimaFicha])

  const handleProfileSelect = async (p: ProfileType) => {
    setProfileLocal(p)
    if (session_id) {
      try {
        await apiSetProfile(session_id, p)
      } catch (e) {
        // T-04-02-05: defense-in-depth backend GUARD-03 igual rechaza si profile null.
        // Frontend warn-only, no toast (UI-SPEC §Tone).
        console.warn('setProfile API failed', e)
      }
    }
  }

  // 2026-05-14 — Ref-guard pre-React state para evitar duplicados de
  // sendQuery cuando el usuario clickea rápido un chip 2× antes que
  // sseState pase a busy. setState es async (~16ms batch) — refs son
  // síncronos. Bug UAT Fabiola: 2 me-bubbles "Congestión nasal + tos
  // productiva" enviadas a las 09:37 + 09:37 (idéntico timestamp).
  const inFlightRef = useRef(false)

  const sendQuery = async (text: string, perfilForzado?: ProfileType) => {
    if (!creds) {
      alert(
        'Tablet sin credenciales. Configura device_id + device_token en localStorage para demo.',
      )
      return
    }
    // Hotfix UAT 2026-05-08 (Issue A) — defense-in-depth profile gate.
    const perfilEnvio = perfilForzado ?? profile
    if (perfilEnvio === null) return
    const trimmed = text.trim()
    if (!trimmed) return
    // Ref-guard sync: si ya hay una query en vuelo, descartar este click.
    // Sync porque setState es async — entre 2 clicks rápidos el isBusy
    // aún no se propaga al chip disabled.
    if (inFlightRef.current) return
    inFlightRef.current = true
    lastInputRef.current = trimmed
    setInput('')
    try {
      await sendChat(trimmed, {
        profile: perfilEnvio,
        device_id: creds.device_id,
        device_token: creds.device_token,
        session_id: idDelCaso(),
      })
    } finally {
      // Liberar el guard al completar (éxito, error o timeout). El SSE
      // stream del store ya manejó el state final (complete/error/cancelled).
      inFlightRef.current = false
    }
  }

  /** 2026-10-01 — el id del caso lo genera la tablet al primer envío y dura hasta "Nuevo
   *  cliente". Antes nunca se guardaba: cada pregunta llegaba sin sesión y el asistente no
   *  recordaba la anterior. */
  function idDelCaso(): string {
    const actual = useSessionStore.getState().session_id
    if (actual) return actual
    const id = nuevoId()
    useSessionStore.getState().setSessionId(id)
    return id
  }

  const handleAccion = (accion: AccionCaso, extra: { forma?: string; peso_kg?: number } = {}) => {
    if (!creds || profile === null || inFlightRef.current) return
    const pedido =
      accion === 'mas_barato' ? 'Más barato' : accion === 'promocion' ? 'Promoción' : accion === 'margen' ? 'Más margen'
        : accion === 'dosis_peso' ? `Pesa ${extra.peso_kg} kg` : extra.forma ? `En ${extra.forma}` : 'Otra forma'
    inFlightRef.current = true
    accionar(pedido, () =>
      pedirAccion({ device_id: creds.device_id, device_token: creds.device_token, session_id: idDelCaso(), accion, ...extra }),
    ).finally(() => {
      inFlightRef.current = false
    })
  }

  // "Nuevo cliente": se limpia al instante y queda "Deshacer" unos segundos (en una fila de
  // clientes no hay tiempo para confirmar; un toque equivocado se revierte).
  const [deshacer, setDeshacer] = useState<null | { messages: Message[]; session_id: string | null; profile: ProfileType | null; timer: number }>(null)
  const handleNuevoCliente = () => {
    const prev = { messages: useChatStore.getState().messages, session_id: useSessionStore.getState().session_id, profile }
    if (prev.messages.length === 0) return
    resetChat()
    useSessionStore.getState().clear()
    setInput('')
    if (deshacer) window.clearTimeout(deshacer.timer)
    const timer = window.setTimeout(() => {
      if (prev.session_id) resetSession(prev.session_id).catch(() => undefined)
      setDeshacer(null)
    }, 6000)
    setDeshacer({ ...prev, timer })
  }
  const handleDeshacer = () => {
    if (!deshacer) return
    window.clearTimeout(deshacer.timer)
    restaurar(deshacer.messages)
    if (deshacer.session_id) useSessionStore.getState().setSessionId(deshacer.session_id)
    useSessionStore.getState().setProfile(deshacer.profile)
    setDeshacer(null)
  }

  // 2026-10-08 — Elvio: tocar un perfil con la consulta empezada = otro cliente. Se abre una consulta
  // nueva con ese perfil (el caso anterior no se mezcla); "Deshacer" la recupera si fue sin querer.
  const handleProfileClick = async (p: ProfileType) => {
    if (useChatStore.getState().messages.length > 0) {
      handleNuevoCliente()
      setProfileLocal(p)
      return
    }
    await handleProfileSelect(p)
  }

  // "+" junto a Paciente: con consulta empezada es "Nuevo cliente" (con Deshacer); sin mensajes,
  // solo se suelta el perfil elegido para marcar el del próximo paciente.
  const handleNuevoPaciente = () => {
    if (useChatStore.getState().messages.length > 0) handleNuevoCliente()
    else setProfileLocal(null)
  }

  const handleSend = () => sendQuery(input)

  const handleRetry = async () => {
    if (lastInputRef.current) await sendQuery(lastInputRef.current)
  }

  /** 2026-05-08 — Click en alternativa o complemento dispara nueva consulta
   *  sobre ese SKU/PA. Reemplaza el ex-comportamiento de "agregar al cart"
   *  (cart eliminado: este demo es consulta, no venta). */
  const handlePickAlternative = async (text: string) => {
    // 2026-10-07: "¿Es para un niño? Tocá su edad": el chip pone el perfil del paciente y
    // repite la consulta anterior con ese perfil (antes se mandaba como texto y quedaba en bucle)
    const perfilChip = PERFIL_POR_CHIP[text]
    if (perfilChip) {
      await handleProfileSelect(perfilChip)
      const anterior = [...useChatStore.getState().messages]
        .reverse()
        .find((m) => m.role === 'me' && m.text && !PERFIL_POR_CHIP[m.text])
      if (anterior?.text) sendQuery(anterior.text, perfilChip)
      return
    }
    sendQuery(text)
  }

  /** 2026-05-12 — Welcome chips ahora ENVÍAN directo al click (atajo).
   *  Antes solo seteaban el input y la regente debía apretar send manualmente,
   *  lo que generaba la percepción de "se corta y queda pensando" reportada
   *  por Elvio en UAT.
   *
   *  Fallback graceful: si no hay perfil seleccionado, dejamos el texto en
   *  el input + scroll al top para que la regente vea el row "Selecciona
   *  quién es el paciente" pulsante. Cuando seleccione perfil, puede apretar
   *  send. Evita "no pasa nada al click" sin feedback. */
  const handleStarterSelect = (query: string) => {
    if (profile === null) {
      setInput(query)
      // scroll al top — el row ProfileChips tiene animate-pulse activo
      // cuando profile es null, así la regente nota qué le falta.
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    sendQuery(query)
  }

  const handleQuickChipPick = (chip: string) => {
    setInput((v) => (v ? `${v} ${chip}` : chip))
  }

  /** 2026-05-12 — fluidez: chip "¿Algo más para este cliente?" post-card
   *  focusea el textarea del Composer sin enviar query. Atajo a continuar
   *  consultando sobre el mismo paciente. */
  const handleContinue = () => {
    const el = document.querySelector<HTMLTextAreaElement>(
      'textarea[aria-label="Mensaje al asistente"]',
    )
    if (el) {
      el.focus()
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const contenido = !started ? (
    <WelcomeScreen onSend={handleStarterSelect} profile={profile} />
  ) : (
    <ChatStream
      messages={messages}
      sseState={sseState}
      onRetry={handleRetry}
      onPickAlternative={handlePickAlternative}
      onContinue={handleContinue}
      device_id={creds?.device_id}
      fichaEnPanel={conFicha}
      fichaActiva={fichaIdx}
      onVerFicha={setFichaIdx}
      perfil={profile}
      onAccion={handleAccion}
      onNuevoCliente={handleNuevoCliente}
    />
  )

  const composer = (
    <Composer
      value={input}
      onChange={setInput}
      onSend={handleSend}
      sseState={sseState}
      profile={profile}
      disabled={profile === null}
      disabledReason="Elegí primero el perfil del paciente"
      onCodigo={handleStarterSelect}
    />
  )

  return (
    <div className={`app app-layout${conPanel ? ' con-panel' : ''}${conFicha ? ' con-ficha' : ''}`}>
      <div className="app-header">
        <Header />
      </div>

      {conPanel && (
        <PanelPaciente
          profile={profile}
          onSelectProfile={handleProfileClick}
          onNuevoPaciente={handleNuevoPaciente}
          motivos={QUICK_CHIPS}
          onMotivo={handleStarterSelect}
          ocupado={isBusy}
        />
      )}

      <main className="app-centro">
        {!conPanel && <ProfileChips profile={profile} onSelectProfile={handleProfileClick} />}
        {!started && conPanel ? (
          // Pantalla ancha, antes de la primera pregunta: solo la pregunta y el campo al medio.
          // Los atajos viven en el panel izquierdo (no se repiten acá).
          <div className="inicio-ancho">
            <h1 className="inicio-titulo">¿Qué necesita el cliente?</h1>
            <p className="inicio-sub">Síntoma, marca, principio activo o receta. O tocá un motivo a la izquierda.</p>
            <div className="inicio-campo">{composer}</div>
          </div>
        ) : (
          contenido
        )}
        {started && !conPanel && <QuickChipsRow chips={QUICK_CHIPS} onPick={handleQuickChipPick} />}
        {(started || !conPanel) && composer}
      </main>

      {conFicha && (
        <FichaPanel
          mensaje={fichaIdx !== null ? messages[fichaIdx] ?? null : null}
          device_id={creds?.device_id}
          onPick={handlePickAlternative}
          ocupado={isBusy}
          acciones={fichaIdx !== null && fichaIdx === messages.length - 1 && !isBusy && messages[fichaIdx]?.card
            ? <AccionesProducto card={messages[fichaIdx].card} perfil={profile} ocupado={isBusy} onAccion={handleAccion} />
            : undefined}
        />
      )}

      {deshacer && (
        <div className="toast-deshacer" role="status" aria-live="polite">
          <span>Nuevo cliente. La consulta anterior se cerró.</span>
          <button type="button" onClick={handleDeshacer}>Deshacer</button>
        </div>
      )}
    </div>
  )
}
